import { useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReCAPTCHA from "react-google-recaptcha";
import { Phone, Mail, MessageSquareText, MapPin, Send, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { business, services, serviceRegions } from "@/data/site";
import { GoogleBadge } from "@/components/common/GoogleBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const initialState = {
  name: "",
  phone: "",
  email: "",
  service: "",
  preferred: "Call",
  message: "",
};

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState({
    ...initialState,
    service: location.state?.service || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const serviceAreas = useMemo(() => serviceRegions.flatMap((r) => r.areas), []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.error("Please add your name and a phone number or email.");
      return;
    }
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      toast.error("Please verify that you're not a robot.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, recaptchaToken: captchaToken });
      setDone(true);
      setForm(initialState);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      toast.success("Request sent! We'll reach out shortly.");
    } catch (err) {
      const msg =
        err?.response?.status === 400
          ? "Verification failed. Please complete the 'I'm not a robot' check again."
          : "Something went wrong. Please call or text us at " + business.phone;
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title="Request service or a quote"
        description="Tell us what's going on with your equipment and the best way to reach you. Prefer to talk? Call or text anytime."
      />

      <section className="section-py">
        <div className="mx-auto grid max-w-7xl gap-10 container-px lg:grid-cols-5">
          {/* Form */}
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl border border-border bg-card p-7 shadow-soft sm:p-9">
              {done ? (
                <div className="flex flex-col items-center justify-center py-14 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/15 text-accent">
                    <CheckCircle2 className="h-9 w-9" />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-foreground">Thank you!</h3>
                  <p className="mt-2 max-w-sm text-muted-foreground">
                    Your request has been received. {business.owner} will get back to you soon. For
                    anything urgent, call or text {business.phone}.
                  </p>
                  <Button variant="outlineDark" className="mt-7" size="lg" onClick={() => setDone(false)}>
                    Send another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Your name *</Label>
                      <Input id="name" value={form.name} onChange={update("name")} placeholder="Jane Doe" className="h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="814-000-0000" className="h-12" />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred">Preferred contact</Label>
                      <Select value={form.preferred} onValueChange={(v) => setForm((f) => ({ ...f, preferred: v }))}>
                        <SelectTrigger id="preferred" className="h-12">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {["Call", "Text", "Email"].map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">Service needed</Label>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger id="service" className="h-12">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                        ))}
                        <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">How can we help?</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Describe the issue, the equipment, or what you're looking for..."
                      className="min-h-[140px] resize-none"
                    />
                  </div>

                  {RECAPTCHA_SITE_KEY && (
                    <div className="flex justify-center pt-1" data-testid="contact-recaptcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token)}
                        onExpired={() => setCaptchaToken(null)}
                      />
                    </div>
                  )}

                  <Button type="submit" variant="brand" size="xl" className="w-full" disabled={submitting} data-testid="contact-submit-btn">
                    {submitting ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="h-5 w-5" /> Send request</>
                    )}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    We respect your privacy. Your details are only used to respond to your request.
                  </p>
                </form>
              )}
            </div>
          </Reveal>

          {/* Sidebar */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-5">
              <div className="rounded-3xl bg-brand-deep p-8 text-white">
                <h3 className="text-xl font-bold">Talk to {business.owner}</h3>
                <p className="mt-2 text-sm text-white/65">
                  Call or text directly with any issue or concern on any powered or non-powered
                  mobility device.
                </p>
                <div className="mt-6 space-y-3">
                  <a href={business.phoneHref} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 transition-colors hover:border-primary/50">
                    <Phone className="h-5 w-5 text-primary" />
                    <span className="font-semibold">{business.phone}</span>
                  </a>
                  <a href={business.smsHref} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 transition-colors hover:border-primary/50">
                    <MessageSquareText className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Text us</span>
                  </a>
                  <a href={business.emailHref} className="flex items-center gap-3 break-all rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 transition-colors hover:border-primary/50">
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">{business.email}</span>
                  </a>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1.5 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4 text-accent" /> Veteran Owned LLC
                </div>
              </div>

              <GoogleBadge />

              <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
                <h3 className="flex items-center gap-2 text-lg font-bold text-foreground">
                  <MapPin className="h-5 w-5 text-primary" /> Where we serve
                </h3>
                <ul className="mt-4 space-y-2 text-[15px] text-muted-foreground">
                  {serviceAreas.map((a) => (
                    <li key={a} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
